import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { BarChart, LineChart, PieChart, AreaChart } from './charts';
import { TableModule, CardModule, FeedModule, ListModule, MetricsModule } from './modules';

const ModuleContainer = ({ 
  moduleType, 
  moduleId, 
  title, 
  data, 
  config = {}, 
  isLoading = false, 
  error = null,
  onRefresh,
  onExport,
  onConfig,
  className = ''
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const renderModuleContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 theme-secondary">Loading...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-red-500 text-4xl mb-2">⚠️</div>
          <p className="theme-primary text-center">{error}</p>
          {onRefresh && (
            <button
              onClick={() => onRefresh(moduleId)}
              className="mt-3 px-4 py-2 theme-accent text-white rounded hover:opacity-80"
            >
              Retry
            </button>
          )}
        </div>
      );
    }

    // Handle React elements (our chart components)
    if (React.isValidElement(data)) {
      return data;
    }

    if (!data || data.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="theme-tertiary text-4xl mb-2">📊</div>
          <p className="theme-secondary text-center">No data available</p>
          {onRefresh && (
            <button
              onClick={() => onRefresh(moduleId)}
              className="mt-3 px-4 py-2 theme-accent text-white rounded hover:opacity-80"
            >
              Refresh
            </button>
          )}
        </div>
      );
    }

    switch (moduleType) {
      case 'Chart':
        const chartType = config.chartType || 'bar';
        const ChartComponent = chartType === 'line' ? LineChart : 
                               chartType === 'pie' ? PieChart :
                               chartType === 'area' ? AreaChart : BarChart;
        return (
          <ChartComponent
            data={data}
            title={null}
            showLabels={config.showLabels !== false}
            animate={config.animate !== false}
          />
        );

      case 'Table':
        return <TableModule data={data} config={config} />;
      case 'Card':
        return <CardModule data={data} config={config} />;
      case 'List':
        return <ListModule data={data} config={config} />;
      case 'Metrics':
        return <MetricsModule data={data} config={config} />;
      case 'Feed':
        return <FeedModule data={data} config={config} />;
      default:
        return (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-500">Module type "{moduleType}" not implemented</p>
          </div>
        );
    }
  };

  const getModuleActions = () => {
    const actions = [];
    
    if (onRefresh) {
      actions.push({
        icon: '🔄',
        label: 'Refresh',
        onClick: () => onRefresh(moduleId)
      });
    }
    
    if (onExport) {
      actions.push({
        icon: '📥',
        label: 'Export',
        onClick: () => onExport(moduleId)
      });
    }
    
    if (onConfig) {
      actions.push({
        icon: '⚙️',
        label: 'Configure',
        onClick: () => onConfig(moduleId)
      });
    }

    return actions;
  };

  return (
    <div className={`theme-bg rounded-lg shadow-md border theme-border-secondary overflow-hidden ${className}`}>
      {/* Module Header */}
      <div 
        className="p-4 border-b theme-border-secondary cursor-pointer hover:theme-bg-secondary transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 rounded-full theme-accent" />
            <h3 className="text-lg font-semibold theme-primary">{title}</h3>
            {data && (
              <span className="text-sm theme-tertiary">
                ({Array.isArray(data) ? data.length : Object.keys(data).length} items)
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {getModuleActions().map((action, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                className="p-2 theme-secondary hover:theme-primary hover:theme-bg-secondary rounded transition-colors"
                title={action.label}
              >
                <span className="text-lg">{action.icon}</span>
              </div>
            ))}
            <button
              className={`p-2 theme-secondary hover:theme-primary transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            >
              <span>▼</span>
            </button>
          </div>
        </div>
      </div>

      {/* Module Content */}
      <div className={`transition-all duration-300 ${isExpanded ? 'max-h-96 overflow-y-auto' : ''}`}>
        <div className="p-4">
          {renderModuleContent()}
        </div>
      </div>

      {/* Module Footer */}
      {config.showFooter && (
        <div className="p-4 border-t theme-border-secondary theme-bg-secondary">
          <div className="flex items-center justify-between text-sm theme-secondary">
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
            <span>Module ID: {moduleId}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleContainer;
